import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../../utils/format';
import Icon from '../../../components/AppIcon';

const PayHistory = ({ historyData }) => {
  const { t } = useTranslation();
  const { eur } = useFormatters();

  const getChangeIcon = (type) => {
    switch (type) {
      case 'promotion':
        return { icon: 'TrendingUp', color: 'text-success' };
      case 'adjustment':
        return { icon: 'RefreshCw', color: 'text-primary' };
      case 'bonus':
        return { icon: 'Award', color: 'text-accent' };
      case 'review':
        return { icon: 'Calendar', color: 'text-muted-foreground' };
      default:
        return { icon: 'DollarSign', color: 'text-foreground' };
    }
  };

  const getChangeTypeLabel = (type) => {
    switch (type) {
      case 'promotion':
        return t('employeePortal.history.eventPromotion');
      case 'adjustment':
        return t('employeePortal.history.eventAdjustment');
      case 'bonus':
        return t('employeePortal.history.eventBonus');
      case 'review':
        return t('employeePortal.history.eventReview');
      default:
        return t('employeePortal.history.eventPayChange');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          {t('employeePortal.history.cardTitle')}
        </h2>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Clock" size={16} />
          <span>{t('employeePortal.history.last24Months')}</span>
        </div>
      </div>

      {/* Upcoming Review */}
      {historyData?.upcomingReview && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Calendar" size={20} color="white" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-foreground">
                {t('employeePortal.history.upcomingReviewTitle')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('employeePortal.history.scheduledFor', {
                  date: historyData?.upcomingReview?.date,
                  type: t('employeePortal.history.reviewType'),
                })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-primary">
                {t('employeePortal.history.daysCount', { count: historyData?.upcomingReview?.daysUntil })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="space-y-6">
        {historyData?.timeline?.map((event, index) => {
          const changeIcon = getChangeIcon(event?.type);
          return (
            <div key={event?.id} className="relative">
              {index < historyData?.timeline?.length - 1 && (
                <div className="absolute left-5 top-12 w-0.5 h-16 bg-border"></div>
              )}
              <div className="flex items-start space-x-4">
                <div className={`w-10 h-10 rounded-full border-2 border-background shadow-sm flex items-center justify-center ${
                  event?.type === 'promotion' ? 'bg-success' :
                  event?.type === 'bonus' ? 'bg-accent' :
                  event?.type === 'adjustment' ? 'bg-primary' : 'bg-muted'
                }`}>
                  <Icon
                    name={changeIcon?.icon}
                    size={16}
                    color={event?.type === 'review' ? 'var(--color-muted-foreground)' : 'white'}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-foreground">{getChangeTypeLabel(event?.type)}</h3>
                    <span className="text-sm text-muted-foreground">{event?.date}</span>
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">{event?.description}</p>

                  {event?.financialDetails && (
                    <div className="bg-muted/30 rounded-lg p-3 mt-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {event?.financialDetails?.previousAmount && (
                          <div>
                            <span className="text-xs text-muted-foreground">
                              {t('employeePortal.history.finPrevious')}
                            </span>
                            <p className="font-medium text-foreground">{eur(event?.financialDetails?.previousAmount)}</p>
                          </div>
                        )}
                        {event?.financialDetails?.newAmount && (
                          <div>
                            <span className="text-xs text-muted-foreground">
                              {t('employeePortal.history.finNew')}
                            </span>
                            <p className="font-medium text-foreground">{eur(event?.financialDetails?.newAmount)}</p>
                          </div>
                        )}
                        {event?.financialDetails?.increase && (
                          <div className="md:col-span-2">
                            <span className="text-xs text-muted-foreground">
                              {t('employeePortal.history.finIncrease')}
                            </span>
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-success">{eur(event?.financialDetails?.increase)}</p>
                              <span className="text-sm text-success">({event?.financialDetails?.percentage}%)</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {event?.additionalInfo && (
                    <div className="flex items-center space-x-2 mt-2">
                      <Icon name="Info" size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{event?.additionalInfo}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="mt-8 pt-6 border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-success">{historyData?.summary?.totalIncreases}</p>
            <p className="text-sm text-muted-foreground">
              {t('employeePortal.history.totalIncreases')}
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{historyData?.summary?.averageIncrease}%</p>
            <p className="text-sm text-muted-foreground">
              {t('employeePortal.history.averageIncrease')}
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-accent">{historyData?.summary?.yearsWithCompany}</p>
            <p className="text-sm text-muted-foreground">
              {t('employeePortal.history.yearsWithCompany')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayHistory;
